/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1_SignUpSignIn",
    forgotPassword: "b2c_1_reset",
    editProfile: "B2C_1_Edit_Profile",
  },
  authorities: {
    signUpSignIn: {
      authority: `${process.env.REACT_APP_B2C_ENDPOINT}/B2C_1_SignUpSignIn`,
    },
    forgotPassword: {
      authority: `${process.env.REACT_APP_B2C_ENDPOINT}/b2c_1_reset`,
    },
    editProfile: {
      authority: `${process.env.REACT_APP_B2C_ENDPOINT}/B2C_1_Edit_Profile`,
    },
  },
  authorityDomain: `${process.env.REACT_APP_B2C_AUTHORITY_DOMAIN}`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      piiLoggingEnabled: false,
      logLevel: LogLevel.Verbose,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [`${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`],
  redirectStartPage: "/list",
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiLogin: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/login`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiSetup: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/setup`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessages: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/messages`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessagesGroup: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/messages/group`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessageCreate: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/messages`,
    method: "POST",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessageUpdate: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/messages`,
    method: "PUT",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessageDelete: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/messages`,
    method: "DELETE",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiMessageDownload: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/download`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiTranscriptUrl: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/transcript`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiManagedUsers: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/manage/users`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiGroupCreate: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/manage/groups`,
    method: "POST",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiGroupDelete: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/manage/groups`,
    method: "DELETE",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiManagedGroups: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/manage/groups`,
    method: "GET",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
  apiGroupMembershipUpdate: {
    endpoint: `${process.env.REACT_APP_API_ENDPOINT}/manage/membership`,
    method: "PUT",
    scopes: [
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/read`,
      `${process.env.REACT_APP_API_SCOPES_ENDPOINT}/write`,
    ], // e.g. api://xxxxxx/access_as_user
  },
};
